import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSelectedCity } from '../hooks/useSelectedCity';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import { normalizeParkingListResponse, type ParkingListItem } from '../lib/parkingList';
import {
  findPricingTemplateForParking,
  normalizePricingTemplates,
  resolveChargingPeriodMinutes,
  resolveVehicleRateFromPricing,
  templateToPricing,
  type ParkingPricingTemplate,
} from '../lib/parkingPricing';
import {
  clearStoredParkingPaymentSession,
  persistParkingPaymentSession,
  readStoredParkingPaymentSessions,
  type StoredParkingPaymentSession,
} from '../lib/parkingPaymentSession';
import {
  addParkingSessionForCurrentUser,
  modifyParkingSessionForCurrentUser,
  normalizeParkingSessionApiResponse,
} from '../lib/parkingSessionApi';
import { getVehicleTypeLabel, readStoredVehicles, type StoredVehicle } from '../lib/vehicles';

const MIN_MINUTES = 10;
const HOUR_IN_MINUTES = 60;
const MAX_HOURS = 12;
const MAX_MINUTES = HOUR_IN_MINUTES * MAX_HOURS;
const STEP_MINUTES = 1;
const DIAL_SIZE = 360;
const DIAL_CENTER = DIAL_SIZE / 2;
const TRACK_RADIUS = 146;
const TRACK_CIRCUMFERENCE = 2 * Math.PI * TRACK_RADIUS;
const DEGREES_PER_MINUTE = 360 / HOUR_IN_MINUTES;
const MIN_DIAL_DEGREES = MIN_MINUTES * DEGREES_PER_MINUTE;
const MAX_DIAL_DEGREES = MAX_MINUTES * DEGREES_PER_MINUTE;
const INNER_TICK_COUNT = 24;
const INNER_TICK_OUTER_RADIUS = 104;
const INNER_TICK_INNER_RADIUS = 78;

const PRICE_FORMATTER = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('pl-PL', {
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

type PaymentMode = 'prepaid' | 'start_stop';

type CompletedParkingPaymentSession = StoredParkingPaymentSession & {
  endedAt: string;
};

type PaymentActionFeedback =
  | {
      type: 'success' | 'error';
      message: string;
    }
  | null;

export type ParkingPaymentParkingContext = Pick<ParkingListItem, 'id' | 'name' | 'address' | 'city' | 'priceTemplateId'>;

type ParkingPaymentPanelProps = {
  fixedParking?: ParkingPaymentParkingContext | null;
  cityLabel?: string | null;
  loginReturnPath?: string;
  eyebrow?: string;
  heading?: string;
  className?: string;
};

const PAYMENT_SELECT_CLASSNAME =
  'w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-[inset_0_1px_0_rgba(148,163,184,0.25)] outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 [color-scheme:light] [&>option]:bg-white [&>option]:text-slate-900 dark:border-white/10 dark:bg-[#262626] dark:text-slate-100 dark:focus:ring-emerald-500/10 dark:[color-scheme:dark] dark:[&>option]:bg-[#262626] dark:[&>option]:text-slate-100';

const PAYMENT_SELECT_OPTION_CLASSNAME = 'bg-white text-slate-900 dark:bg-[#262626] dark:text-slate-100';

export function ParkingPaymentPanel({
  fixedParking = null,
  cityLabel: cityLabelProp,
  loginReturnPath = '/',
  eyebrow = 'Opłać postój',
  heading = 'Kupowanie biletu',
  className,
}: ParkingPaymentPanelProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const activePrepaidSvgRef = useRef<SVGSVGElement | null>(null);
  const lastPointerAngleRef = useRef<number | null>(null);
  const prepaidExtensionLastPointerAngleRef = useRef<number | null>(null);
  const minutesRef = useRef(MIN_MINUTES);
  const dialDegreesRef = useRef(MIN_DIAL_DEGREES);
  const prepaidExtensionDraftMinutesRef = useRef(0);
  const prepaidExtensionDraftDegreesRef = useRef(0);
  const prepaidExtensionMinMinutesRef = useRef(0);
  const prepaidExtensionMaxMinutesRef = useRef(MAX_MINUTES);
  const { token, isAuthenticated } = useAuth();
  const { selectedCityId, selectedCityName } = useSelectedCity();
  const fixedParkingId = useMemo(() => normalizeIdValue(fixedParking?.id), [fixedParking]);
  const isFixedParking = Boolean(fixedParkingId);
  const [parkingOptions, setParkingOptions] = useState<ParkingListItem[]>([]);
  const [pricingTemplates, setPricingTemplates] = useState<ParkingPricingTemplate[]>([]);
  const [isParkingLoading, setIsParkingLoading] = useState(false);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [parkingError, setParkingError] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<StoredVehicle[]>([]);
  const [selectedParkingId, setSelectedParkingId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('prepaid');
  const [minutes, setMinutes] = useState(MIN_MINUTES);
  const [dialDegrees, setDialDegrees] = useState(MIN_DIAL_DEGREES);
  const [isDragging, setIsDragging] = useState(false);
  const [isPrepaidExtensionMode, setIsPrepaidExtensionMode] = useState(false);
  const [prepaidExtensionBaseRemainingMinutes, setPrepaidExtensionBaseRemainingMinutes] = useState(0);
  const [prepaidExtensionDraftRemainingMinutes, setPrepaidExtensionDraftRemainingMinutes] = useState(0);
  const [isPrepaidExtensionDragging, setIsPrepaidExtensionDragging] = useState(false);
  const [storedSessions, setStoredSessions] = useState<StoredParkingPaymentSession[]>(() => readStoredParkingPaymentSessions());
  const [recentlyStoppedSession, setRecentlyStoppedSession] = useState<CompletedParkingPaymentSession | null>(null);
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);
  const [isSessionSubmitting, setIsSessionSubmitting] = useState(false);
  const [isStopSubmitting, setIsStopSubmitting] = useState(false);
  const [sessionActionError, setSessionActionError] = useState<string | null>(null);
  const [isPrepaidSubmitting, setIsPrepaidSubmitting] = useState(false);
  const [prepaidActionFeedback, setPrepaidActionFeedback] = useState<PaymentActionFeedback>(null);
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  const activeSession = useMemo(
    () => resolveStoredParkingSessionForContext(storedSessions, fixedParkingId, selectedParkingId),
    [fixedParkingId, selectedParkingId, storedSessions]
  );
  const upsertStoredSession = useCallback((session: StoredParkingPaymentSession) => {
    setStoredSessions((current) => upsertStoredParkingSessions(current, session));
  }, []);
  const removeStoredSession = useCallback((sessionId: string) => {
    setStoredSessions((current) => current.filter((session) => session.id !== sessionId));
  }, []);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    setVehicles(readStoredVehicles());
  }, []);

  useEffect(() => {
    if (!activeSession) {
      return;
    }

    setPaymentMode(activeSession.sessionMode);
    setSelectedVehicleId(activeSession.vehicleId);
    if (!fixedParkingId) {
      setSelectedParkingId(activeSession.parkingId);
    }
  }, [activeSession, fixedParkingId]);

  useEffect(() => {
    setPrepaidActionFeedback(null);
  }, [paymentMode, selectedParkingId, selectedVehicleId, minutes]);

  useEffect(() => {
    let isCancelled = false;

    const fetchPricingTemplates = async () => {
      if (!isAuthenticated) {
        setPricingTemplates([]);
        setPricingError(null);
        setIsPricingLoading(false);
        return;
      }

      setIsPricingLoading(true);
      setPricingError(null);

      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-prices',
          },
          {
            jwtToken: token,
            useStoredToken: isAuthenticated,
          }
        );

        if (isCancelled) {
          return;
        }

        setPricingTemplates(normalizePricingTemplates(response.json));
      } catch (error) {
        if (isCancelled) {
          return;
        }

        const message = error instanceof Error && error.message ? error.message : 'Nie udało się pobrać cenników parkingów.';
        setPricingError(message);
        setPricingTemplates([]);
      } finally {
        if (!isCancelled) {
          setIsPricingLoading(false);
        }
      }
    };

    void fetchPricingTemplates();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, token]);

  useEffect(() => {
    let isCancelled = false;

    const fetchParkingOptions = async () => {
      if (!isAuthenticated) {
        setParkingOptions([]);
        setParkingError(null);
        setIsParkingLoading(false);
        return;
      }

      if (isFixedParking) {
        setParkingOptions([]);
        setParkingError(null);
        setIsParkingLoading(false);
        return;
      }

      setIsParkingLoading(true);
      setParkingError(null);

      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-list',
          },
          {
            jwtToken: token,
            useStoredToken: isAuthenticated,
          }
        );

        if (isCancelled) {
          return;
        }

        const allParkings = normalizeParkingListResponse(response.json);
        const filteredParkings = allParkings.filter((item) => {
          if (!selectedCityId) {
            return true;
          }

          const itemCity = normalizeIdValue(item.city);
          const currentCity = normalizeIdValue(selectedCityId);
          return !itemCity || !currentCity ? true : itemCity === currentCity;
        });

        setParkingOptions(filteredParkings);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        const message = error instanceof Error && error.message ? error.message : 'Nie udało się pobrać listy parkingów.';
        setParkingError(message);
        setParkingOptions([]);
      } finally {
        if (!isCancelled) {
          setIsParkingLoading(false);
        }
      }
    };

    void fetchParkingOptions();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, isFixedParking, selectedCityId, token]);

  useEffect(() => {
    if (fixedParkingId) {
      setSelectedParkingId(fixedParkingId);
      return;
    }

    setSelectedParkingId((current) => {
      const normalizedCurrent = normalizeIdValue(current);
      if (normalizedCurrent && parkingOptions.some((item) => normalizeIdValue(item.id) === normalizedCurrent)) {
        return current;
      }

      if (storedSessions.length === 1) {
        return storedSessions[0].parkingId;
      }

      return '';
    });
  }, [fixedParkingId, parkingOptions, storedSessions]);

  useEffect(() => {
    setSelectedVehicleId((current) => {
      if (activeSession) {
        return activeSession.vehicleId;
      }

      return vehicles.some((item) => item.id === current) ? current : '';
    });
  }, [activeSession, vehicles]);

  const snapDialToMinutes = useCallback((nextMinutes: number) => {
    const snappedMinutes = snapMinutes(nextMinutes);
    const nextDegrees = snappedMinutes * DEGREES_PER_MINUTE;

    minutesRef.current = snappedMinutes;
    dialDegreesRef.current = nextDegrees;
    setMinutes(snappedMinutes);
    setDialDegrees(nextDegrees);
  }, []);

  const syncDialFromDegrees = useCallback((nextDegrees: number) => {
    const clampedDegrees = clamp(nextDegrees, MIN_DIAL_DEGREES, MAX_DIAL_DEGREES);
    const nextMinutes = snapMinutes(clampedDegrees / DEGREES_PER_MINUTE);

    dialDegreesRef.current = clampedDegrees;
    minutesRef.current = nextMinutes;
    setDialDegrees(clampedDegrees);
    setMinutes(nextMinutes);
  }, []);

  const finishDrag = useCallback(() => {
    lastPointerAngleRef.current = null;
    setIsDragging(false);
    snapDialToMinutes(minutesRef.current);
  }, [snapDialToMinutes]);

  useEffect(() => {
    const handlePointerUp = () => {
      if (!isDragging) {
        return;
      }

      finishDrag();
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [finishDrag, isDragging]);

  const resolvePointerAngle = useCallback((svg: SVGSVGElement | null, clientX: number, clientY: number) => {
    if (!svg) {
      return null;
    }

    const rect = svg.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const angle = Math.atan2(clientY - centerY, clientX - centerX);

    return (radiansToDegrees(angle) + 90 + 360) % 360;
  }, []);

  const updateDialFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const nextAngle = resolvePointerAngle(svgRef.current, clientX, clientY);
      if (nextAngle === null) {
        return;
      }

      if (lastPointerAngleRef.current === null) {
        lastPointerAngleRef.current = nextAngle;
        return;
      }

      let delta = nextAngle - lastPointerAngleRef.current;
      if (delta > 180) {
        delta -= 360;
      } else if (delta < -180) {
        delta += 360;
      }

      lastPointerAngleRef.current = nextAngle;
      syncDialFromDegrees(dialDegreesRef.current + delta);
    },
    [resolvePointerAngle, syncDialFromDegrees]
  );

  const handleDialPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      lastPointerAngleRef.current = resolvePointerAngle(svgRef.current, event.clientX, event.clientY);
      setIsDragging(true);
    },
    [resolvePointerAngle]
  );

  const handleDialPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (!isDragging) {
        return;
      }

      event.preventDefault();
      updateDialFromPointer(event.clientX, event.clientY);
    },
    [isDragging, updateDialFromPointer]
  );

  const handleDialPointerUp = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      finishDrag();
    },
    [finishDrag]
  );

  const resetPrepaidExtensionState = useCallback(() => {
    prepaidExtensionLastPointerAngleRef.current = null;
    prepaidExtensionDraftMinutesRef.current = 0;
    prepaidExtensionDraftDegreesRef.current = 0;
    prepaidExtensionMinMinutesRef.current = 0;
    prepaidExtensionMaxMinutesRef.current = MAX_MINUTES;
    setIsPrepaidExtensionDragging(false);
    setIsPrepaidExtensionMode(false);
    setPrepaidExtensionBaseRemainingMinutes(0);
    setPrepaidExtensionDraftRemainingMinutes(0);
  }, []);

  const setPrepaidExtensionDraftState = useCallback((nextMinutes: number, minMinutes: number, maxMinutes: number) => {
    const snappedMinutes = snapMinutesWithinRange(nextMinutes, minMinutes, maxMinutes);
    const nextDegrees = snappedMinutes * DEGREES_PER_MINUTE;

    prepaidExtensionDraftMinutesRef.current = snappedMinutes;
    prepaidExtensionDraftDegreesRef.current = nextDegrees;
    setPrepaidExtensionDraftRemainingMinutes(snappedMinutes);
  }, []);

  const syncPrepaidExtensionFromDegrees = useCallback((nextDegrees: number) => {
    const minMinutes = prepaidExtensionMinMinutesRef.current;
    const maxMinutes = prepaidExtensionMaxMinutesRef.current;

    if (minMinutes <= 0 || maxMinutes < minMinutes) {
      return;
    }

    const clampedDegrees = clamp(nextDegrees, minMinutes * DEGREES_PER_MINUTE, maxMinutes * DEGREES_PER_MINUTE);
    const nextMinutes = snapMinutesWithinRange(clampedDegrees / DEGREES_PER_MINUTE, minMinutes, maxMinutes);

    prepaidExtensionDraftDegreesRef.current = clampedDegrees;
    prepaidExtensionDraftMinutesRef.current = nextMinutes;
    setPrepaidExtensionDraftRemainingMinutes(nextMinutes);
  }, []);

  const finishPrepaidExtensionDrag = useCallback(() => {
    prepaidExtensionLastPointerAngleRef.current = null;
    setIsPrepaidExtensionDragging(false);

    const minMinutes = prepaidExtensionMinMinutesRef.current;
    const maxMinutes = prepaidExtensionMaxMinutesRef.current;

    if (minMinutes > 0 && maxMinutes >= minMinutes) {
      setPrepaidExtensionDraftState(prepaidExtensionDraftMinutesRef.current, minMinutes, maxMinutes);
    }
  }, [setPrepaidExtensionDraftState]);

  useEffect(() => {
    const handlePointerUp = () => {
      if (!isPrepaidExtensionDragging) {
        return;
      }

      finishPrepaidExtensionDrag();
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [finishPrepaidExtensionDrag, isPrepaidExtensionDragging]);

  const beginPrepaidExtensionMode = useCallback(() => {
    if (!activeSession?.stopAt) {
      return false;
    }

    const currentRemainingMinutes = Math.max(0, Math.ceil((Date.parse(activeSession.stopAt) - nowTimestamp) / 60000));
    if (currentRemainingMinutes <= 0) {
      return false;
    }

    const baseMinutes =
      isPrepaidExtensionMode && prepaidExtensionBaseRemainingMinutes > 0
        ? prepaidExtensionBaseRemainingMinutes
        : currentRemainingMinutes;
    const maxMinutes = baseMinutes + MAX_MINUTES;
    const initialDraftMinutes =
      isPrepaidExtensionMode && prepaidExtensionDraftMinutesRef.current >= baseMinutes
        ? prepaidExtensionDraftMinutesRef.current
        : baseMinutes;

    prepaidExtensionMinMinutesRef.current = baseMinutes;
    prepaidExtensionMaxMinutesRef.current = maxMinutes;
    setPrepaidActionFeedback(null);
    setIsPrepaidExtensionMode(true);
    setPrepaidExtensionBaseRemainingMinutes(baseMinutes);
    setPrepaidExtensionDraftState(initialDraftMinutes, baseMinutes, maxMinutes);

    return true;
  }, [
    activeSession?.stopAt,
    nowTimestamp,
    isPrepaidExtensionMode,
    prepaidExtensionBaseRemainingMinutes,
    setPrepaidExtensionDraftState,
  ]);

  const updatePrepaidExtensionDialFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const nextAngle = resolvePointerAngle(activePrepaidSvgRef.current, clientX, clientY);
      if (nextAngle === null) {
        return;
      }

      if (prepaidExtensionLastPointerAngleRef.current === null) {
        prepaidExtensionLastPointerAngleRef.current = nextAngle;
        return;
      }

      let delta = nextAngle - prepaidExtensionLastPointerAngleRef.current;
      if (delta > 180) {
        delta -= 360;
      } else if (delta < -180) {
        delta += 360;
      }

      prepaidExtensionLastPointerAngleRef.current = nextAngle;
      syncPrepaidExtensionFromDegrees(prepaidExtensionDraftDegreesRef.current + delta);
    },
    [resolvePointerAngle, syncPrepaidExtensionFromDegrees]
  );

  const handleActivePrepaidDialPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (isPrepaidSubmitting || !beginPrepaidExtensionMode()) {
        return;
      }

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      prepaidExtensionLastPointerAngleRef.current = resolvePointerAngle(activePrepaidSvgRef.current, event.clientX, event.clientY);
      setIsPrepaidExtensionDragging(true);
    },
    [beginPrepaidExtensionMode, isPrepaidSubmitting, resolvePointerAngle]
  );

  const handleActivePrepaidDialPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (!isPrepaidExtensionDragging) {
        return;
      }

      event.preventDefault();
      updatePrepaidExtensionDialFromPointer(event.clientX, event.clientY);
    },
    [isPrepaidExtensionDragging, updatePrepaidExtensionDialFromPointer]
  );

  const handleActivePrepaidDialPointerUp = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      finishPrepaidExtensionDrag();
    },
    [finishPrepaidExtensionDrag]
  );

  const handleCancelPrepaidExtension = useCallback(() => {
    setPrepaidActionFeedback(null);
    resetPrepaidExtensionState();
  }, [resetPrepaidExtensionState]);

  const handleDialKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        snapDialToMinutes(minutesRef.current + STEP_MINUTES);
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        snapDialToMinutes(minutesRef.current - STEP_MINUTES);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        snapDialToMinutes(MIN_MINUTES);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        snapDialToMinutes(MAX_MINUTES);
      }
    },
    [snapDialToMinutes]
  );

  const currentHourMinutes = useMemo(() => {
    const minutesFromDial = Math.max(MIN_MINUTES, dialDegrees / DEGREES_PER_MINUTE);
    const remainder = minutesFromDial % HOUR_IN_MINUTES;

    if (remainder === 0 && minutesFromDial > 0) {
      return HOUR_IN_MINUTES;
    }

    return remainder;
  }, [dialDegrees]);

  const progressRatio = currentHourMinutes / HOUR_IN_MINUTES;
  const progressDegrees = progressRatio * 360;
  const progressLength = TRACK_CIRCUMFERENCE * progressRatio;
  const handleAngle = degreesToRadians(progressDegrees - 90);
  const handleX = DIAL_CENTER + TRACK_RADIUS * Math.cos(handleAngle);
  const handleY = DIAL_CENTER + TRACK_RADIUS * Math.sin(handleAngle);

  const activeSessionIsPrepaid = activeSession?.sessionMode === 'prepaid';
  const activeSessionStopAt = useMemo(
    () => (activeSessionIsPrepaid && activeSession?.stopAt ? new Date(activeSession.stopAt) : null),
    [activeSession, activeSessionIsPrepaid]
  );
  const endAt = useMemo(() => new Date(nowTimestamp + minutes * 60000), [minutes, nowTimestamp]);
  const endTimeLabel = useMemo(() => TIME_FORMATTER.format(endAt), [endAt]);
  const endDateLabel = useMemo(() => DATE_FORMATTER.format(endAt), [endAt]);
  const displayDuration = useMemo(() => formatDuration(minutes), [minutes]);
  const cityLabel = cityLabelProp ?? selectedCityName ?? 'Centrum';
  const isPrepaidMode = paymentMode === 'prepaid';
  const isStartStopMode = paymentMode === 'start_stop';
  const availableParkingOptions = useMemo(() => {
    if (!fixedParking) {
      return parkingOptions;
    }

    const fixedOption: ParkingListItem = {
      id: fixedParking.id,
      name: fixedParking.name ?? null,
      address: fixedParking.address ?? null,
      city: fixedParking.city ?? null,
      priceTemplateId: fixedParking.priceTemplateId ?? null,
      stats: null,
      spotOccupancy: null,
      coords: null,
      space_regular: null,
      space_handi: null,
      space_family: null,
      space_disabled: null,
      space_ev: null,
    };

    const matchedOption = fixedParkingId
      ? parkingOptions.find((item) => normalizeIdValue(item.id) === fixedParkingId) ?? null
      : null;

    return matchedOption ? [matchedOption] : [fixedOption];
  }, [fixedParking, fixedParkingId, parkingOptions]);
  const selectedParking = useMemo(
    () => availableParkingOptions.find((item) => String(item.id ?? '') === selectedParkingId) ?? null,
    [availableParkingOptions, selectedParkingId]
  );
  const headerBadgeLabel = 'Miasto';
  const headerBadgeValue = cityLabel;
  const hasHeading = heading.trim().length > 0;
  const selectedVehicle = useMemo(() => vehicles.find((item) => item.id === selectedVehicleId) ?? null, [selectedVehicleId, vehicles]);
  const selectedPricingTemplate = useMemo(
    () => findPricingTemplateForParking(selectedParking, pricingTemplates),
    [pricingTemplates, selectedParking]
  );
  const selectedPricing = useMemo(
    () => (selectedPricingTemplate ? templateToPricing(selectedPricingTemplate) : null),
    [selectedPricingTemplate]
  );
  const selectedVehicleTypeLabel = useMemo(
    () => (selectedVehicle ? getVehicleTypeLabel(selectedVehicle.vehicleType) : null),
    [selectedVehicle]
  );
  const chargingPeriodMinutes = useMemo(() => resolveChargingPeriodMinutes(selectedPricing), [selectedPricing]);
  const selectedVehicleRate = useMemo(
    () => (selectedVehicle ? resolveVehicleRateFromPricing(selectedPricing, selectedVehicle.vehicleType) : null),
    [selectedPricing, selectedVehicle]
  );
  const billingPeriodLabel = useMemo(
    () => (chargingPeriodMinutes !== null ? formatDuration(chargingPeriodMinutes) : null),
    [chargingPeriodMinutes]
  );
  const parkingFee = useMemo(() => {
    if (chargingPeriodMinutes === null || selectedVehicleRate === null) {
      return 0;
    }

    return roundCurrency((minutes / chargingPeriodMinutes) * selectedVehicleRate);
  }, [chargingPeriodMinutes, minutes, selectedVehicleRate]);
  const totalPrice = useMemo(() => roundCurrency(parkingFee), [parkingFee]);
  const vehicleStepLabel = `Krok ${isFixedParking ? 1 : 2}`;
  const paymentModeStepLabel = `Krok ${isFixedParking ? 2 : 3}`;
  const actionStepLabel = `Krok ${isFixedParking ? 3 : 4}`;
  const compactDesktopHeaderClass = isFixedParking ? 'lg:px-4 lg:py-4' : '';
  const compactDesktopTitleClass = isFixedParking ? 'lg:mt-1 lg:text-[1.65rem]' : '';
  const compactDesktopBadgeClass = isFixedParking ? 'lg:px-3 lg:py-2' : '';
  const compactDesktopContentClass = isFixedParking ? 'lg:gap-4 lg:px-4 lg:py-4' : '';
  const compactDesktopPanelRadiusClass = isFixedParking ? 'lg:rounded-2xl' : '';
  const compactDesktopCardClass = isFixedParking ? 'lg:px-3 lg:py-3' : '';
  const compactDesktopInnerCardClass = isFixedParking ? 'lg:px-3 lg:py-3' : '';
  const compactDesktopButtonClass = isFixedParking ? 'lg:px-4 lg:py-3 lg:text-sm' : '';
  const compactDesktopSelectClass = isFixedParking ? 'lg:px-3 lg:py-2.5 lg:text-xs' : '';
  const compactDesktopDialClass = isFixedParking ? 'lg:max-w-[15rem]' : '';
  const compactDesktopDisplayValueClass = isFixedParking ? 'lg:text-[clamp(2.4rem,4.2vw,4rem)]' : '';
  const compactDesktopClockValueClass = isFixedParking ? 'lg:text-xl' : '';
  const compactDesktopFeeValueClass = isFixedParking ? 'lg:text-base' : '';
  const compactDesktopSummaryTextClass = isFixedParking ? 'lg:text-xs' : '';
  const paymentConfigurationMessage = useMemo(() => {
    if (!selectedParking || !selectedVehicle) {
      return isFixedParking ? 'Wybierz pojazd, aby ustawić czas postoju.' : 'Wybierz parking i pojazd, aby ustawić czas postoju.';
    }

    if (isPricingLoading && pricingTemplates.length === 0) {
      return 'Trwa pobieranie cennika parkingu.';
    }

    if (pricingError) {
      return pricingError;
    }

    if (!selectedPricingTemplate) {
      return 'Dla wybranego parkingu nie znaleziono cennika.';
    }

    if (chargingPeriodMinutes === null) {
      return 'Wybrany cennik nie ma poprawnego okresu naliczania.';
    }

    if (selectedVehicleRate === null) {
      return `Brak stawki dla typu pojazdu: ${selectedVehicleTypeLabel ?? 'pojazd'}.`;
    }

    return null;
  }, [
    chargingPeriodMinutes,
    isPricingLoading,
    pricingError,
    pricingTemplates.length,
    selectedParking,
    selectedPricingTemplate,
    selectedVehicle,
    selectedVehicleRate,
    selectedVehicleTypeLabel,
    isFixedParking,
  ]);
  const canConfigureSelection = Boolean(selectedParking && selectedVehicle && paymentConfigurationMessage === null);
  const sessionStartedAt = useMemo(() => (activeSession ? new Date(activeSession.startedAt) : null), [activeSession]);
  const activeSessionElapsedMs = useMemo(
    () => (sessionStartedAt ? Math.max(0, nowTimestamp - sessionStartedAt.getTime()) : 0),
    [nowTimestamp, sessionStartedAt]
  );
  const activeSessionElapsedMinutes = useMemo(
    () => (sessionStartedAt ? Math.max(1, Math.ceil(activeSessionElapsedMs / 60000)) : 0),
    [activeSessionElapsedMs, sessionStartedAt]
  );
  const activeSessionElapsedLabel = useMemo(() => formatElapsedDuration(activeSessionElapsedMs), [activeSessionElapsedMs]);
  const activeSessionElapsedSummary = useMemo(
    () => (activeSessionElapsedMinutes > 0 ? formatDuration(activeSessionElapsedMinutes) : '0 min'),
    [activeSessionElapsedMinutes]
  );
  const activeSessionStartedLabel = useMemo(
    () => (activeSession ? DATE_TIME_FORMATTER.format(new Date(activeSession.startedAt)) : null),
    [activeSession]
  );
  const activeSessionFee = useMemo(() => {
    if (!activeSession || activeSessionIsPrepaid) {
      return 0;
    }

    return roundCurrency((activeSessionElapsedMinutes / activeSession.chargingPeriodMinutes) * activeSession.vehicleRate);
  }, [activeSession, activeSessionElapsedMinutes, activeSessionIsPrepaid]);
  const activePrepaidRemainingMs = useMemo(() => {
    if (!activeSessionIsPrepaid || !activeSessionStopAt) {
      return 0;
    }

    return Math.max(0, activeSessionStopAt.getTime() - nowTimestamp);
  }, [activeSessionIsPrepaid, activeSessionStopAt, nowTimestamp]);
  const activePrepaidRemainingMinutes = useMemo(
    () => (activePrepaidRemainingMs > 0 ? Math.max(1, Math.ceil(activePrepaidRemainingMs / 60000)) : 0),
    [activePrepaidRemainingMs]
  );
  const activePrepaidRemainingSummary = useMemo(
    () => (activePrepaidRemainingMinutes > 0 ? formatDuration(activePrepaidRemainingMinutes) : '0 min'),
    [activePrepaidRemainingMinutes]
  );
  const activePrepaidEndsLabel = useMemo(
    () => (activeSessionStopAt ? DATE_TIME_FORMATTER.format(activeSessionStopAt) : null),
    [activeSessionStopAt]
  );
  const prepaidDisplayedRemainingMinutes = useMemo(
    () => (isPrepaidExtensionMode ? prepaidExtensionDraftRemainingMinutes : activePrepaidRemainingMinutes),
    [activePrepaidRemainingMinutes, isPrepaidExtensionMode, prepaidExtensionDraftRemainingMinutes]
  );
  const prepaidDisplayedRemainingMs = useMemo(
    () => (isPrepaidExtensionMode ? prepaidDisplayedRemainingMinutes * 60000 : activePrepaidRemainingMs),
    [activePrepaidRemainingMs, isPrepaidExtensionMode, prepaidDisplayedRemainingMinutes]
  );
  const prepaidExtensionMinutes = useMemo(
    () =>
      isPrepaidExtensionMode
        ? Math.max(0, prepaidDisplayedRemainingMinutes - prepaidExtensionBaseRemainingMinutes)
        : 0,
    [isPrepaidExtensionMode, prepaidDisplayedRemainingMinutes, prepaidExtensionBaseRemainingMinutes]
  );
  const prepaidDisplayedRemainingLabel = useMemo(
    () => formatElapsedDuration(prepaidDisplayedRemainingMs),
    [prepaidDisplayedRemainingMs]
  );
  const prepaidDisplayedRemainingSummary = useMemo(() => {
    if (!isPrepaidExtensionMode) {
      return activePrepaidRemainingSummary;
    }

    if (prepaidExtensionMinutes > 0) {
      return `+ ${formatDuration(prepaidExtensionMinutes)}`;
    }

    return 'Obroc pokretlo, aby dolozyc czas.';
  }, [activePrepaidRemainingSummary, isPrepaidExtensionMode, prepaidExtensionMinutes]);
  const prepaidDisplayedStopAt = useMemo(() => {
    if (!activeSessionStopAt) {
      return null;
    }

    if (!isPrepaidExtensionMode || prepaidExtensionMinutes <= 0) {
      return activeSessionStopAt;
    }

    return new Date(activeSessionStopAt.getTime() + prepaidExtensionMinutes * 60000);
  }, [activeSessionStopAt, isPrepaidExtensionMode, prepaidExtensionMinutes]);
  const prepaidDisplayedEndsLabel = useMemo(
    () => (prepaidDisplayedStopAt ? DATE_TIME_FORMATTER.format(prepaidDisplayedStopAt) : null),
    [prepaidDisplayedStopAt]
  );
  const prepaidExtensionFee = useMemo(() => {
    if (!activeSession || prepaidExtensionMinutes <= 0) {
      return 0;
    }

    return roundCurrency((prepaidExtensionMinutes / activeSession.chargingPeriodMinutes) * activeSession.vehicleRate);
  }, [activeSession, prepaidExtensionMinutes]);
  const activePrepaidCurrentHourMinutes = useMemo(() => {
    if (prepaidDisplayedRemainingMinutes <= 0) {
      return 0;
    }

    const remainder = prepaidDisplayedRemainingMinutes % HOUR_IN_MINUTES;
    return remainder === 0 ? HOUR_IN_MINUTES : remainder;
  }, [prepaidDisplayedRemainingMinutes]);
  const activePrepaidDialDegrees = prepaidDisplayedRemainingMinutes * DEGREES_PER_MINUTE;
  const activePrepaidProgressRatio = clamp(activePrepaidCurrentHourMinutes / HOUR_IN_MINUTES, 0, 1);
  const activePrepaidProgressDegrees = activePrepaidProgressRatio * 360;
  const activePrepaidProgressLength = TRACK_CIRCUMFERENCE * activePrepaidProgressRatio;
  const activePrepaidHandleAngle = degreesToRadians(activePrepaidProgressDegrees - 90);
  const activePrepaidHandleX = DIAL_CENTER + TRACK_RADIUS * Math.cos(activePrepaidHandleAngle);
  const activePrepaidHandleY = DIAL_CENTER + TRACK_RADIUS * Math.sin(activePrepaidHandleAngle);
  const stoppedSessionEndedLabel = useMemo(
    () => (recentlyStoppedSession ? DATE_TIME_FORMATTER.format(new Date(recentlyStoppedSession.endedAt)) : null),
    [recentlyStoppedSession]
  );
  const stoppedSessionElapsedMs = useMemo(() => {
    if (!recentlyStoppedSession) {
      return 0;
    }

    return Math.max(0, Date.parse(recentlyStoppedSession.endedAt) - Date.parse(recentlyStoppedSession.startedAt));
  }, [recentlyStoppedSession]);
  const stoppedSessionElapsedMinutes = useMemo(
    () => (recentlyStoppedSession ? Math.max(1, Math.ceil(stoppedSessionElapsedMs / 60000)) : 0),
    [recentlyStoppedSession, stoppedSessionElapsedMs]
  );
  const stoppedSessionElapsedLabel = useMemo(() => formatElapsedDuration(stoppedSessionElapsedMs), [stoppedSessionElapsedMs]);
  const stoppedSessionElapsedSummary = useMemo(
    () => (stoppedSessionElapsedMinutes > 0 ? formatDuration(stoppedSessionElapsedMinutes) : '0 min'),
    [stoppedSessionElapsedMinutes]
  );
  const stoppedSessionFee = useMemo(() => {
    if (!recentlyStoppedSession) {
      return 0;
    }

    return roundCurrency((stoppedSessionElapsedMinutes / recentlyStoppedSession.chargingPeriodMinutes) * recentlyStoppedSession.vehicleRate);
  }, [recentlyStoppedSession, stoppedSessionElapsedMinutes]);
  useEffect(() => {
    if (!activeSession || !activeSessionIsPrepaid || !activeSessionStopAt || activePrepaidRemainingMs > 0) {
      return;
    }

    resetPrepaidExtensionState();
    clearStoredParkingPaymentSession(activeSession.id);
    removeStoredSession(activeSession.id);
    setPrepaidActionFeedback({
      type: 'success',
      message: 'Oplacony postoj dobiegl konca.',
    });
  }, [activePrepaidRemainingMs, activeSession, activeSessionIsPrepaid, activeSessionStopAt, removeStoredSession, resetPrepaidExtensionState]);
  useEffect(() => {
    if (activeSessionIsPrepaid) {
      return;
    }

    resetPrepaidExtensionState();
  }, [activeSessionIsPrepaid, resetPrepaidExtensionState]);
  const handlePaymentModeChange = useCallback(
    (nextMode: PaymentMode) => {
      if (activeSession) {
        return;
      }

      setPrepaidActionFeedback(null);
      setSessionActionError(null);
      setPaymentMode(nextMode);
      if (nextMode !== 'start_stop') {
        setRecentlyStoppedSession(null);
        setIsStopModalOpen(false);
      }
    },
    [activeSession]
  );

  const handleCreatePrepaidSession = useCallback(async () => {
    if (!selectedParking || !selectedVehicle || chargingPeriodMinutes === null || selectedVehicleRate === null) {
      return;
    }

    const resolvedParkingId = normalizeIdValue(selectedParking.id ?? selectedParkingId);
    if (!resolvedParkingId) {
      return;
    }

    setIsPrepaidSubmitting(true);
    setPrepaidActionFeedback(null);

    try {
      const startedAt = new Date().toISOString();
      const stopAt = endAt.toISOString();
      const response = await addParkingSessionForCurrentUser({
        plate: selectedVehicle.licensePlate,
        parkingId: resolvedParkingId,
        startTime: startedAt,
        stopTime: stopAt,
      });
      const normalizedResponse = normalizeParkingSessionApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? 'API nie potwierdzilo utworzenia oplaconej sesji parkingowej.');
      }

      if (!normalizedResponse.sessionId) {
        throw new Error('API nie zwrocilo session_id dla oplaconej sesji parkingowej.');
      }

      const nextSession: StoredParkingPaymentSession = {
        id: normalizedResponse.sessionId,
        sessionMode: 'prepaid',
        parkingId: resolvedParkingId,
        parkingName: selectedParking.name ?? selectedParking.address ?? `Parking ${resolvedParkingId}`,
        parkingAddress: selectedParking.address ?? null,
        vehicleId: selectedVehicle.id,
        vehicleLicensePlate: selectedVehicle.licensePlate,
        vehicleBrand: selectedVehicle.brand,
        vehicleModel: selectedVehicle.model,
        vehicleType: selectedVehicle.vehicleType,
        pricingTemplateId: normalizeIdValue(selectedPricingTemplate?.id) ?? null,
        pricingTemplateName: selectedPricingTemplate?.name ?? null,
        chargingPeriodMinutes,
        vehicleRate: selectedVehicleRate,
        startedAt,
        stopAt,
      };

      persistParkingPaymentSession(nextSession);
      upsertStoredSession(nextSession);
      setRecentlyStoppedSession(null);
      setPaymentMode('prepaid');
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Nie udalo sie utworzyc oplaconej sesji parkingowej.';
      setPrepaidActionFeedback({
        type: 'error',
        message,
      });
    } finally {
      setIsPrepaidSubmitting(false);
    }
  }, [
    chargingPeriodMinutes,
    endAt,
    selectedParking,
    selectedParkingId,
    selectedPricingTemplate,
    selectedVehicle,
    selectedVehicleRate,
    upsertStoredSession,
  ]);

  const handleExtendPrepaidSession = useCallback(async () => {
    if (!activeSession || !activeSession.stopAt || prepaidExtensionMinutes <= 0 || !prepaidDisplayedStopAt) {
      return;
    }

    setIsPrepaidSubmitting(true);
    setPrepaidActionFeedback(null);

    try {
      const nextStopAt = prepaidDisplayedStopAt.toISOString();
      const response = await modifyParkingSessionForCurrentUser({
        sessionId: activeSession.id,
        stopTime: nextStopAt,
      });
      const normalizedResponse = normalizeParkingSessionApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? 'API nie potwierdzilo wydluzenia oplaconej sesji parkingowej.');
      }

      const nextSession: StoredParkingPaymentSession = {
        ...activeSession,
        stopAt: nextStopAt,
      };

      persistParkingPaymentSession(nextSession);
      upsertStoredSession(nextSession);
      resetPrepaidExtensionState();
      setPrepaidActionFeedback({
        type: 'success',
        message: 'Czas postoju zostal wydluzony.',
      });
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Nie udalo sie wydluzyc oplaconej sesji parkingowej.';
      setPrepaidActionFeedback({
        type: 'error',
        message,
      });
    } finally {
      setIsPrepaidSubmitting(false);
    }
  }, [activeSession, prepaidDisplayedStopAt, prepaidExtensionMinutes, resetPrepaidExtensionState, upsertStoredSession]);

  const handleStartStopSession = useCallback(async () => {
    if (!selectedParking || !selectedVehicle || chargingPeriodMinutes === null || selectedVehicleRate === null) {
      return;
    }

    const resolvedParkingId = normalizeIdValue(selectedParking.id ?? selectedParkingId);
    if (!resolvedParkingId) {
      return;
    }

    setIsSessionSubmitting(true);
    setSessionActionError(null);

    try {
      const startedAt = new Date().toISOString();
      const response = await addParkingSessionForCurrentUser({
        plate: selectedVehicle.licensePlate,
        parkingId: resolvedParkingId,
        startTime: startedAt,
        stopTime: '',
      });
      const normalizedResponse = normalizeParkingSessionApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? 'API nie potwierdzilo utworzenia sesji parkingowej.');
      }

      if (!normalizedResponse.sessionId) {
        throw new Error('API nie zwrocilo session_id dla sesji parkingowej.');
      }

      const nextSession: StoredParkingPaymentSession = {
        id: normalizedResponse.sessionId,
        sessionMode: 'start_stop',
        parkingId: resolvedParkingId,
        parkingName: selectedParking.name ?? selectedParking.address ?? `Parking ${resolvedParkingId}`,
        parkingAddress: selectedParking.address ?? null,
        vehicleId: selectedVehicle.id,
        vehicleLicensePlate: selectedVehicle.licensePlate,
        vehicleBrand: selectedVehicle.brand,
        vehicleModel: selectedVehicle.model,
        vehicleType: selectedVehicle.vehicleType,
        pricingTemplateId: normalizeIdValue(selectedPricingTemplate?.id) ?? null,
        pricingTemplateName: selectedPricingTemplate?.name ?? null,
        chargingPeriodMinutes,
        vehicleRate: selectedVehicleRate,
        startedAt,
        stopAt: null,
      };

      persistParkingPaymentSession(nextSession);
      upsertStoredSession(nextSession);
      setRecentlyStoppedSession(null);
      setPaymentMode('start_stop');
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : 'Nie udalo sie utworzyc sesji parkingowej.';
      setSessionActionError(message);
    } finally {
      setIsSessionSubmitting(false);
    }
  }, [
    chargingPeriodMinutes,
    selectedParking,
    selectedParkingId,
    selectedPricingTemplate,
    selectedVehicle,
    selectedVehicleRate,
    setSessionActionError,
    upsertStoredSession,
  ]);

  const handleConfirmStopSession = useCallback(async () => {
    if (!activeSession) {
      return;
    }

    setIsStopSubmitting(true);
    setSessionActionError(null);

    try {
      const endedAt = new Date().toISOString();
      const response = await modifyParkingSessionForCurrentUser({
        sessionId: activeSession.id,
        stopTime: endedAt,
      });
      const normalizedResponse = normalizeParkingSessionApiResponse(response.json);

      if (normalizedResponse.status !== null && normalizedResponse.status !== 1) {
        throw new Error(normalizedResponse.message ?? 'API nie potwierdzilo zakonczenia sesji parkingowej.');
      }

      const nextStoppedSession: CompletedParkingPaymentSession = {
        ...activeSession,
        stopAt: endedAt,
        endedAt,
      };

      clearStoredParkingPaymentSession(activeSession.id);
      removeStoredSession(activeSession.id);
      setRecentlyStoppedSession(nextStoppedSession);
      setIsStopModalOpen(false);
      setSessionActionError(null);
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : 'Nie udalo sie zakonczyc sesji parkingowej.';
      setSessionActionError(message);
    } finally {
      setIsStopSubmitting(false);
    }
  }, [activeSession, removeStoredSession]);

  return (
    <>
      <section
        className={[
          'overflow-hidden rounded-2xl border border-white/70 bg-white/92 shadow-[0_32px_90px_-45px_rgba(15,23,42,0.45)] backdrop-blur transition-colors dark:border-white/10 dark:bg-[#1f1f1f]/96 dark:shadow-[0_32px_90px_-45px_rgba(0,0,0,0.8)] lg:flex lg:h-full lg:flex-col',
          compactDesktopPanelRadiusClass,
          className ?? '',
        ].join(' ')}
      >
          <div className={`border-b border-slate-200/80 bg-[radial-gradient(circle_at_top,#bbf7d0_0%,#f8fafc_58%,#ffffff_100%)] px-5 py-5 dark:border-white/10 dark:bg-[radial-gradient(circle_at_top,#24583f_0%,#1f3429_16%,#1f1f1f_62%,#1f1f1f_100%)] ${compactDesktopHeaderClass} ${isFixedParking ? 'lg:hidden' : ''}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-700 dark:text-emerald-200/85">{eyebrow}</p>
                {hasHeading ? <h1 className={`mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white ${compactDesktopTitleClass}`}>{heading}</h1> : null}
              </div>
              {!isFixedParking ? (
                <div className={`rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-right shadow-[0_18px_36px_-32px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-white/[0.05] dark:shadow-none ${compactDesktopBadgeClass}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">{headerBadgeLabel}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">{headerBadgeValue}</p>
                </div>
              ) : null}
            </div>
          </div>

          <div className={`flex flex-col gap-6 px-5 py-6 sm:px-6 sm:py-7 lg:flex-1 ${compactDesktopContentClass}`}>
            {!isAuthenticated ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 px-5 py-6 text-center shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none">
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">Logowanie wymagane</p>
                <p className="mt-3 text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                  {isFixedParking ? 'Aby opłacić ten parking, zaloguj się na konto.' : 'Aby opłacić postój, zaloguj się na konto.'}
                </p>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">
                  {isFixedParking ? 'Po zalogowaniu wrócisz do płatności na tym parkingu.' : 'Po zalogowaniu wrócisz do kupowania biletu dla wybranego parkingu.'}
                </p>
                <Link
                  to="/login"
                  state={{ from: loginReturnPath }}
                  className="mt-5 inline-flex items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#059669_0%,#10b981_48%,#14b8a6_100%)] px-5 py-3 text-sm font-semibold text-white shadow-[0_22px_40px_-24px_rgba(16,185,129,0.6)] transition hover:scale-[1.01] hover:shadow-[0_26px_46px_-24px_rgba(16,185,129,0.72)] active:scale-[0.99]"
                >
                  Zaloguj się
                </Link>
              </div>
            ) : null}

            {isAuthenticated ? (
              activeSessionIsPrepaid ? (
                <>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/75 px-5 py-6 shadow-[0_18px_36px_-30px_rgba(16,185,129,0.25)] dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:shadow-none">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-emerald-700 dark:text-emerald-200/80">Aktywna sesja</p>
                        <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                          {isPrepaidExtensionMode ? 'Dodawanie czasu' : 'Oplacony postoj'}
                        </h2>
                      </div>
                      <span className="rounded-2xl border border-emerald-200 bg-white/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:border-emerald-400/20 dark:bg-white/[0.06] dark:text-emerald-200">
                        {activeSession.vehicleLicensePlate}
                      </span>
                    </div>

                        <div className="mt-6 text-center">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">
                            {isPrepaidExtensionMode ? 'Nowy pozostaly czas' : 'Pozostaly czas'}
                          </p>
                          <p className="mt-3 min-h-[1.1em] whitespace-nowrap text-[clamp(2.9rem,14vw,5.25rem)] font-semibold leading-[0.95] tracking-[-0.045em] tabular-nums text-slate-900 dark:text-white">
                            {prepaidDisplayedRemainingLabel}
                          </p>
                          <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">{prepaidDisplayedRemainingSummary}</p>
                        </div>

                        <div className="mt-2">
                          <div className={`relative mx-auto aspect-square w-full max-w-[22rem] transition duration-200 ${isPrepaidExtensionDragging ? 'scale-[1.01]' : ''}`}>
                            <svg
                              ref={activePrepaidSvgRef}
                              viewBox={`0 0 ${DIAL_SIZE} ${DIAL_SIZE}`}
                              className={`h-full w-full overflow-visible ${isPrepaidSubmitting ? 'cursor-default' : isPrepaidExtensionDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                              style={{ touchAction: 'none' }}
                              onPointerDown={handleActivePrepaidDialPointerDown}
                              onPointerMove={handleActivePrepaidDialPointerMove}
                              onPointerUp={handleActivePrepaidDialPointerUp}
                              onPointerCancel={handleActivePrepaidDialPointerUp}
                            >
                              <defs>
                                <linearGradient id="parking-payment-dial" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#10b981" />
                                  <stop offset="55%" stopColor="#14b8a6" />
                                  <stop offset="100%" stopColor="#0ea5e9" />
                                </linearGradient>
                                <radialGradient id="parking-payment-wheel" cx="50%" cy="40%" r="65%">
                                  <stop offset="0%" stopColor="#ffffff" />
                                  <stop offset="100%" stopColor="#f1f5f9" />
                                </radialGradient>
                                <radialGradient id="parking-payment-wheel-dark" cx="50%" cy="38%" r="65%">
                                  <stop offset="0%" stopColor="#2b2b2b" />
                                  <stop offset="100%" stopColor="#202020" />
                                </radialGradient>
                              </defs>

                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={TRACK_RADIUS + 18} fill="rgba(255,255,255,0.88)" className="dark:fill-[#262626]" />

                              <g transform={`rotate(-90 ${DIAL_CENTER} ${DIAL_CENTER})`}>
                                <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={TRACK_RADIUS} fill="none" stroke="rgba(226,232,240,0.88)" strokeWidth="18" className="dark:stroke-white/[0.08]" />
                                <circle
                                  cx={DIAL_CENTER}
                                  cy={DIAL_CENTER}
                                  r={TRACK_RADIUS}
                                  fill="none"
                                  stroke="url(#parking-payment-dial)"
                                  strokeWidth="18"
                                  strokeLinecap="round"
                                  strokeDasharray={`${activePrepaidProgressLength} ${TRACK_CIRCUMFERENCE}`}
                                />
                              </g>

                              <g transform={`rotate(${-activePrepaidDialDegrees} ${DIAL_CENTER} ${DIAL_CENTER})`}>
                                <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="url(#parking-payment-wheel)" className="dark:hidden" />
                                <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="url(#parking-payment-wheel-dark)" className="hidden dark:block" />
                                {Array.from({ length: INNER_TICK_COUNT }).map((_, index) => {
                                  const tickAngle = degreesToRadians((index / INNER_TICK_COUNT) * 360 - 90);
                                  const outerX = DIAL_CENTER + INNER_TICK_OUTER_RADIUS * Math.cos(tickAngle);
                                  const outerY = DIAL_CENTER + INNER_TICK_OUTER_RADIUS * Math.sin(tickAngle);
                                  const innerX = DIAL_CENTER + INNER_TICK_INNER_RADIUS * Math.cos(tickAngle);
                                  const innerY = DIAL_CENTER + INNER_TICK_INNER_RADIUS * Math.sin(tickAngle);
                                  const isMajorTick = index % 2 === 0;

                                  return (
                                    <line
                                      key={index}
                                      x1={innerX}
                                      y1={innerY}
                                      x2={outerX}
                                      y2={outerY}
                                      stroke="#0f172a"
                                      strokeWidth={isMajorTick ? 5.5 : 4}
                                      strokeLinecap="round"
                                      className="dark:stroke-slate-100"
                                      opacity={isMajorTick ? 1 : 0.92}
                                    />
                                  );
                                })}
                              </g>

                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="none" stroke="rgba(255,255,255,0.72)" strokeWidth="2" className="dark:stroke-white/[0.04]" />
                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER - TRACK_RADIUS} r={6} fill="#0f172a" className="dark:fill-white" />
                              <circle cx={activePrepaidHandleX} cy={activePrepaidHandleY} r={17} fill="#ffffff" opacity="0.28" />
                              <circle cx={activePrepaidHandleX} cy={activePrepaidHandleY} r={13} fill="url(#parking-payment-dial)" />
                            </svg>
                          </div>
                        </div>
                    <div className="mt-5 grid gap-3">
                      <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Pojazd</p>
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
                          {activeSession.vehicleLicensePlate} • {activeSession.vehicleBrand} {activeSession.vehicleModel}
                        </p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{getVehicleTypeLabel(activeSession.vehicleType)}</p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Parking</p>
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{activeSession.parkingName}</p>
                        {activeSession.parkingAddress ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{activeSession.parkingAddress}</p> : null}
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Oplacono do</p>
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{prepaidDisplayedEndsLabel}</p>
                        {isPrepaidExtensionMode && activePrepaidEndsLabel && prepaidExtensionMinutes > 0 ? (
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">Poprzedni koniec: {activePrepaidEndsLabel}</p>
                        ) : null}
                        {activeSessionStartedLabel ? (
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">Start: {activeSessionStartedLabel}</p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {prepaidActionFeedback ? (
                    <div
                      className={`rounded-2xl border px-4 py-3 text-sm ${
                        prepaidActionFeedback.type === 'success'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200'
                          : 'border-red-200 bg-red-50 text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200'
                      }`}
                    >
                      {prepaidActionFeedback.message}
                    </div>
                  ) : null}

                  {isPrepaidExtensionMode ? (
                    <>
                      <div className="grid grid-cols-1 gap-3">
                        <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-[0_16px_28px_-26px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopInnerCardClass}`}>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Dodatkowa oplata parkingowa</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/75">
                            {selectedVehicleTypeLabel}
                            {selectedVehicleRate !== null && billingPeriodLabel ? ` • ${PRICE_FORMATTER.format(selectedVehicleRate)} / ${billingPeriodLabel}` : ''}
                          </p>
                          <p className={`mt-2 text-lg font-semibold tracking-tight text-slate-900 dark:text-white ${compactDesktopFeeValueClass}`}>{PRICE_FORMATTER.format(prepaidExtensionFee)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={handleCancelPrepaidExtension}
                          disabled={isPrepaidSubmitting}
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-100 dark:hover:bg-white/[0.08]"
                        >
                          Anuluj
                        </button>
                        <button
                          type="button"
                          disabled={isPrepaidSubmitting || prepaidExtensionMinutes <= 0}
                          onClick={() => void handleExtendPrepaidSession()}
                          className="rounded-2xl bg-[linear-gradient(135deg,#059669_0%,#10b981_48%,#14b8a6_100%)] px-5 py-3 text-sm font-semibold text-white shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)] transition hover:scale-[1.01] hover:shadow-[0_30px_55px_-24px_rgba(16,185,129,0.75)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 disabled:hover:shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)]"
                        >
                          {isPrepaidSubmitting
                            ? 'Aktualizowanie...'
                            : prepaidExtensionMinutes > 0
                              ? `${PRICE_FORMATTER.format(prepaidExtensionFee)} • Dodaj czas`
                              : 'Obroc kolo'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-sm text-slate-500 dark:text-slate-300/75">
                      Zlap pokretlo i obroc je, aby dolozyc czas do aktualnej sesji.
                    </p>
                  )}



                  <div className="hidden grid-cols-1 gap-3">
                    <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-[0_16px_28px_-26px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopInnerCardClass}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Dodatkowa oplata parkingowa</p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/75">
                        {selectedVehicleTypeLabel}
                        {selectedVehicleRate !== null && billingPeriodLabel ? ` • ${PRICE_FORMATTER.format(selectedVehicleRate)} / ${billingPeriodLabel}` : ''}
                      </p>
                      <p className={`mt-2 text-lg font-semibold tracking-tight text-slate-900 dark:text-white ${compactDesktopFeeValueClass}`}>{PRICE_FORMATTER.format(totalPrice)}</p>
                    </div>
                  </div>


                  <button
                    type="button"
                    disabled={isPrepaidSubmitting || prepaidActionFeedback?.type === 'success'}
                    onClick={() => void handleCreatePrepaidSession()}
                    className="hidden"
                  >
                    {isPrepaidSubmitting
                      ? 'Aktualizowanie...'
                      : prepaidActionFeedback?.type === 'success'
                        ? 'Czas wydluzony'
                        : `${PRICE_FORMATTER.format(totalPrice)} • Doloz czas`}
                  </button>
                </>
              ) : (
              <>
                <section className="grid gap-3">
                  {!isFixedParking ? (
                  <div className={`rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-4 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopCardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Krok 1</p>
                        <h2 className="mt-1 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                          {isFixedParking ? 'Ten parking' : 'Wybierz parking'}
                        </h2>
                      </div>
                      <span className="rounded-2xl border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-200">
                        {cityLabel}
                      </span>
                    </div>

                    {activeSession ? (
                      <div className={`mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-4 dark:border-emerald-400/20 dark:bg-emerald-500/10 ${compactDesktopInnerCardClass}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-200/80">Parking aktywnej sesji</p>
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{activeSession.parkingName}</p>
                        {activeSession.parkingAddress ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{activeSession.parkingAddress}</p> : null}
                        </div>
                      ) : (
                      <>
                        <label className="mt-4 block">
                          <span className="sr-only">Wybierz parking</span>
                          <select
                            value={selectedParkingId}
                            onChange={(event) => setSelectedParkingId(event.target.value)}
                            className={`${PAYMENT_SELECT_CLASSNAME} ${compactDesktopSelectClass}`}
                            disabled={isParkingLoading || availableParkingOptions.length === 0}
                          >
                            <option value="" className={PAYMENT_SELECT_OPTION_CLASSNAME}>
                              {isParkingLoading ? 'Ładowanie parkingów...' : 'Wybierz parking'}
                            </option>
                            {availableParkingOptions.map((parking) => {
                              const optionValue = String(parking.id ?? '');
                              const optionLabel = parking.address ?? parking.name ?? `Parking ${optionValue}`;

                              return (
                                <option key={optionValue} value={optionValue} className={PAYMENT_SELECT_OPTION_CLASSNAME}>
                                  {optionLabel}
                                </option>
                              );
                            })}
                          </select>
                        </label>

                        {selectedParking ? (
                          <>
                            <p className="mt-3 text-sm text-slate-500 dark:text-slate-300/75">
                              {selectedParking.name ?? 'Parking'}
                              {selectedParking.address ? ` • ${selectedParking.address}` : ''}
                            </p>
                            {selectedPricingTemplate ? (
                              <p className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                Cennik: {selectedPricingTemplate.name ?? `ID ${selectedPricingTemplate.id}`}
                              </p>
                            ) : null}
                          </>
                        ) : null}

                        {parkingError ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{parkingError}</p> : null}
                        {pricingError ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{pricingError}</p> : null}
                      </>
                    )}
                  </div>
                  ) : null}

                  <div className={`rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-4 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopCardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">{vehicleStepLabel}</p>
                        <h2 className="mt-1 text-base font-semibold tracking-tight text-slate-900 dark:text-white">Wybierz pojazd</h2>
                      </div>
                      {!activeSession ? (
                        <Link
                          to="/admin/vehicles/new"
                        className={`rounded-2xl border border-emerald-300 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-100 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20 ${compactDesktopBadgeClass}`}
                        >
                          Dodaj
                        </Link>
                      ) : null}
                    </div>

                    {activeSession ? (
                      <div className={`mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-4 dark:border-emerald-400/20 dark:bg-emerald-500/10 ${compactDesktopInnerCardClass}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-200/80">Pojazd aktywnej sesji</p>
                        <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
                          {activeSession.vehicleLicensePlate} • {activeSession.vehicleBrand} {activeSession.vehicleModel}
                        </p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{getVehicleTypeLabel(activeSession.vehicleType)}</p>
                      </div>
                    ) : (
                      <>
                        <label className="mt-4 block">
                          <span className="sr-only">Wybierz pojazd</span>
                          <select
                            value={selectedVehicleId}
                            onChange={(event) => setSelectedVehicleId(event.target.value)}
                            className={`${PAYMENT_SELECT_CLASSNAME} ${compactDesktopSelectClass}`}
                            disabled={vehicles.length === 0}
                          >
                            <option value="" className={PAYMENT_SELECT_OPTION_CLASSNAME}>
                              {vehicles.length ? 'Wybierz pojazd' : 'Brak zapisanych pojazdów'}
                            </option>
                            {vehicles.map((vehicle) => (
                              <option key={vehicle.id} value={vehicle.id} className={PAYMENT_SELECT_OPTION_CLASSNAME}>
                                {vehicle.licensePlate} • {vehicle.brand} {vehicle.model} • {getVehicleTypeLabel(vehicle.vehicleType)}
                              </option>
                            ))}
                          </select>
                        </label>

                        {vehicles.length === 0 ? (
                          <p className="mt-3 text-sm text-slate-500 dark:text-slate-300/75">
                            Najpierw dodaj pojazd, aby można było opłacić postój.
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className={`rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-4 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopCardClass}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">{paymentModeStepLabel}</p>
                    <h2 className="mt-1 text-base font-semibold tracking-tight text-slate-900 dark:text-white">Wybierz typ opłaty</h2>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handlePaymentModeChange('prepaid')}
                        disabled={Boolean(activeSession)}
                        className={`rounded-2xl border px-4 py-4 text-left transition ${compactDesktopInnerCardClass} ${
                          isPrepaidMode
                            ? 'border-emerald-300 bg-emerald-50 shadow-[0_18px_32px_-24px_rgba(16,185,129,0.25)] dark:border-emerald-400/30 dark:bg-emerald-500/10'
                            : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-slate-50 dark:border-white/[0.08] dark:bg-white/[0.035] dark:hover:border-emerald-400/35 dark:hover:bg-white/[0.06]'
                        } ${activeSession ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Opłata z góry</p>
                      </button>

                      <button
                        type="button"
                        onClick={() => handlePaymentModeChange('start_stop')}
                        className={`rounded-2xl border px-4 py-4 text-left transition ${compactDesktopInnerCardClass} ${
                          isStartStopMode
                            ? 'border-emerald-300 bg-emerald-50 shadow-[0_18px_32px_-24px_rgba(16,185,129,0.25)] dark:border-emerald-400/30 dark:bg-emerald-500/10'
                            : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-slate-50 dark:border-white/[0.08] dark:bg-white/[0.035] dark:hover:border-emerald-400/35 dark:hover:bg-white/[0.06]'
                        }`}
                      >
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Start / stop</p>
                      </button>
                    </div>
                  </div>
                </section>

                {isPrepaidMode ? (
                  !canConfigureSelection ? (
                    <div className={`rounded-2xl border border-dashed border-slate-300 bg-white/75 px-5 py-6 text-center shadow-[0_18px_36px_-30px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none ${compactDesktopCardClass}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">{actionStepLabel}</p>
                      <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">{paymentConfigurationMessage}</p>
                    </div>
                  ) : (
                    <>
                      <div className="mx-auto w-full max-w-[24rem] px-2 text-center">
                        <p className={`text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75 ${isFixedParking ? 'lg:hidden' : ''}`}>{actionStepLabel}</p>
                        <p className={`mt-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/75 ${isFixedParking ? 'lg:hidden' : ''}`}>Czas postoju</p>
                        <p className={`mt-3 min-h-[1.1em] whitespace-nowrap text-[clamp(2.9rem,14vw,5.25rem)] font-semibold leading-[0.95] tracking-[-0.045em] tabular-nums text-slate-900 dark:text-white ${compactDesktopDisplayValueClass}`}>
                          {displayDuration}
                        </p>
                      </div>

                      <div className={`rounded-2xl border border-slate-200 bg-slate-50/90 px-4 py-4 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] transition-colors dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopCardClass}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-100">
                            <ClockIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Koniec postoju</p>
                            <p className={`mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white ${compactDesktopClockValueClass}`}>{endTimeLabel}</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{endDateLabel}</p>
                          </div>
                        </div>
                      </div>

                      <div
                        className="touch-none outline-none"
                        role="slider"
                        tabIndex={0}
                        aria-label="Czas postoju"
                        aria-valuemin={MIN_MINUTES}
                        aria-valuemax={MAX_MINUTES}
                        aria-valuenow={minutes}
                        aria-valuetext={displayDuration}
                        onKeyDown={handleDialKeyDown}
                      >
                        <div className={`relative mx-auto aspect-square w-full max-w-[22rem] transition duration-200 ${compactDesktopDialClass} ${isDragging ? 'scale-[1.01]' : ''}`}>
                          <svg
                            ref={svgRef}
                            viewBox={`0 0 ${DIAL_SIZE} ${DIAL_SIZE}`}
                            className={`h-full w-full overflow-visible ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                            style={{ touchAction: 'none' }}
                            onPointerDown={handleDialPointerDown}
                            onPointerMove={handleDialPointerMove}
                            onPointerUp={handleDialPointerUp}
                            onPointerCancel={handleDialPointerUp}
                          >
                            <defs>
                              <linearGradient id="parking-payment-dial" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="55%" stopColor="#14b8a6" />
                                <stop offset="100%" stopColor="#0ea5e9" />
                              </linearGradient>
                              <radialGradient id="parking-payment-wheel" cx="50%" cy="40%" r="65%">
                                <stop offset="0%" stopColor="#ffffff" />
                                <stop offset="100%" stopColor="#f1f5f9" />
                              </radialGradient>
                              <radialGradient id="parking-payment-wheel-dark" cx="50%" cy="38%" r="65%">
                                <stop offset="0%" stopColor="#2b2b2b" />
                                <stop offset="100%" stopColor="#202020" />
                              </radialGradient>
                            </defs>

                            <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={TRACK_RADIUS + 18} fill="rgba(255,255,255,0.88)" className="dark:fill-[#262626]" />

                            <g transform={`rotate(-90 ${DIAL_CENTER} ${DIAL_CENTER})`}>
                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={TRACK_RADIUS} fill="none" stroke="rgba(226,232,240,0.88)" strokeWidth="18" className="dark:stroke-white/[0.08]" />
                              <circle
                                cx={DIAL_CENTER}
                                cy={DIAL_CENTER}
                                r={TRACK_RADIUS}
                                fill="none"
                                stroke="url(#parking-payment-dial)"
                                strokeWidth="18"
                                strokeLinecap="round"
                                strokeDasharray={`${progressLength} ${TRACK_CIRCUMFERENCE}`}
                              />
                            </g>

                            <g transform={`rotate(${dialDegrees} ${DIAL_CENTER} ${DIAL_CENTER})`}>
                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="url(#parking-payment-wheel)" className="dark:hidden" />
                              <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="url(#parking-payment-wheel-dark)" className="hidden dark:block" />
                              {Array.from({ length: INNER_TICK_COUNT }).map((_, index) => {
                                const tickAngle = degreesToRadians((index / INNER_TICK_COUNT) * 360 - 90);
                                const outerX = DIAL_CENTER + INNER_TICK_OUTER_RADIUS * Math.cos(tickAngle);
                                const outerY = DIAL_CENTER + INNER_TICK_OUTER_RADIUS * Math.sin(tickAngle);
                                const innerX = DIAL_CENTER + INNER_TICK_INNER_RADIUS * Math.cos(tickAngle);
                                const innerY = DIAL_CENTER + INNER_TICK_INNER_RADIUS * Math.sin(tickAngle);
                                const isMajorTick = index % 2 === 0;

                                return (
                                  <line
                                    key={index}
                                    x1={innerX}
                                    y1={innerY}
                                    x2={outerX}
                                    y2={outerY}
                                    stroke="#0f172a"
                                    strokeWidth={isMajorTick ? 5.5 : 4}
                                    strokeLinecap="round"
                                    className="dark:stroke-slate-100"
                                    opacity={isMajorTick ? 1 : 0.92}
                                  />
                                );
                              })}
                            </g>

                            <circle cx={DIAL_CENTER} cy={DIAL_CENTER} r={126} fill="none" stroke="rgba(255,255,255,0.72)" strokeWidth="2" className="dark:stroke-white/[0.04]" />
                            <circle cx={DIAL_CENTER} cy={DIAL_CENTER - TRACK_RADIUS} r={6} fill="#0f172a" className="dark:fill-white" />
                            <circle cx={handleX} cy={handleY} r={17} fill="#ffffff" opacity="0.28" />
                            <circle cx={handleX} cy={handleY} r={13} fill="url(#parking-payment-dial)" />
                          </svg>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-[0_16px_28px_-26px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none ${compactDesktopInnerCardClass}`}>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Opłata parkingowa</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/75">
                            {selectedVehicleTypeLabel}
                            {selectedVehicleRate !== null && billingPeriodLabel ? ` • ${PRICE_FORMATTER.format(selectedVehicleRate)} / ${billingPeriodLabel}` : ''}
                          </p>
                          <p className={`mt-2 text-lg font-semibold tracking-tight text-slate-900 dark:text-white ${compactDesktopFeeValueClass}`}>{PRICE_FORMATTER.format(parkingFee)}</p>
                        </div>
                      </div>

                      <p className={`text-center text-sm text-slate-500 dark:text-slate-300/75 ${compactDesktopSummaryTextClass} ${isFixedParking ? 'lg:hidden' : ''}`}>Kwota do zapłaty: {PRICE_FORMATTER.format(totalPrice)}</p>

                      {prepaidActionFeedback ? (
                        <div
                          className={`rounded-2xl border px-4 py-3 text-sm ${
                            prepaidActionFeedback.type === 'success'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200'
                              : 'border-red-200 bg-red-50 text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200'
                          }`}
                        >
                          {prepaidActionFeedback.message}
                        </div>
                      ) : null}

                      <button
                        type="button"
                        disabled={isPrepaidSubmitting || prepaidActionFeedback?.type === 'success'}
                        onClick={() => void handleCreatePrepaidSession()}
                        className={`rounded-2xl bg-[linear-gradient(135deg,#059669_0%,#10b981_48%,#14b8a6_100%)] px-5 py-4 text-base font-semibold text-white shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)] transition hover:scale-[1.01] hover:shadow-[0_30px_55px_-24px_rgba(16,185,129,0.75)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 disabled:hover:shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)] ${compactDesktopButtonClass}`}
                      >
                        {isPrepaidSubmitting
                          ? 'Zapisywanie...'
                          : prepaidActionFeedback?.type === 'success'
                            ? 'Sesja zapisana'
                            : `${PRICE_FORMATTER.format(totalPrice)} • Kontynuuj`}
                      </button>
                    </>
                  )
                ) : isStartStopMode ? (
                  activeSession ? (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 px-5 py-6 shadow-[0_18px_36px_-30px_rgba(16,185,129,0.25)] dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:shadow-none">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-emerald-700 dark:text-emerald-200/80">{actionStepLabel}</p>
                      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Aktywny postój</h2>

                      <div className="mt-5 rounded-2xl border border-emerald-200 bg-white/80 px-4 py-5 text-center dark:border-emerald-400/15 dark:bg-[#202020]">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Czas postoju</p>
                        <p className="mt-3 whitespace-nowrap text-[clamp(2.6rem,13vw,4.6rem)] font-semibold leading-[0.95] tracking-[-0.04em] tabular-nums text-slate-900 dark:text-white">
                          {activeSessionElapsedLabel}
                        </p>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">{activeSessionElapsedSummary}</p>
                      </div>

                      <div className="mt-4 grid gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Rozpoczęto</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{activeSessionStartedLabel}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Parking</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{activeSession.parkingName}</p>
                          {activeSession.parkingAddress ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{activeSession.parkingAddress}</p> : null}
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Pojazd</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
                            {activeSession.vehicleLicensePlate} • {activeSession.vehicleBrand} {activeSession.vehicleModel}
                          </p>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{getVehicleTypeLabel(activeSession.vehicleType)}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Naliczona kwota</p>
                          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{PRICE_FORMATTER.format(activeSessionFee)}</p>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">
                            {PRICE_FORMATTER.format(activeSession.vehicleRate)} / {formatDuration(activeSession.chargingPeriodMinutes)}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSessionActionError(null);
                          setIsStopModalOpen(true);
                        }}
                        className="mt-5 w-full rounded-2xl bg-slate-900 px-5 py-4 text-base font-semibold text-white shadow-[0_22px_40px_-24px_rgba(15,23,42,0.6)] transition hover:scale-[1.01] dark:bg-white dark:text-slate-900"
                      >
                        Zakończ postój
                      </button>
                    </div>
                  ) : recentlyStoppedSession ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/90 px-5 py-6 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">{actionStepLabel}</p>
                      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Postój zakończony</h2>

                      <div className="mt-5 rounded-2xl border border-slate-200 bg-white/85 px-4 py-5 text-center dark:border-white/10 dark:bg-white/[0.04]">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/75">Łączny czas</p>
                        <p className="mt-3 whitespace-nowrap text-[clamp(2.6rem,13vw,4.6rem)] font-semibold leading-[0.95] tracking-[-0.04em] tabular-nums text-slate-900 dark:text-white">
                          {stoppedSessionElapsedLabel}
                        </p>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-300/75">{stoppedSessionElapsedSummary}</p>
                      </div>

                      <div className="mt-4 grid gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Zakończono</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{stoppedSessionEndedLabel}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Parking</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{recentlyStoppedSession.parkingName}</p>
                          {recentlyStoppedSession.parkingAddress ? (
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{recentlyStoppedSession.parkingAddress}</p>
                          ) : null}
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Kwota końcowa</p>
                          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{PRICE_FORMATTER.format(stoppedSessionFee)}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setRecentlyStoppedSession(null)}
                        className="mt-5 w-full rounded-2xl bg-[linear-gradient(135deg,#059669_0%,#10b981_48%,#14b8a6_100%)] px-5 py-4 text-base font-semibold text-white shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)] transition hover:scale-[1.01] hover:shadow-[0_30px_55px_-24px_rgba(16,185,129,0.75)] active:scale-[0.99]"
                      >
                        Rozpocznij nowy postój
                      </button>
                    </div>
                  ) : !canConfigureSelection ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white/75 px-5 py-6 text-center shadow-[0_18px_36px_-30px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">{actionStepLabel}</p>
                      <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">{paymentConfigurationMessage}</p>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/90 px-5 py-6 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.2)] dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">{actionStepLabel}</p>
                      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Rozpocznij naliczanie</h2>

                      <div className="mt-4 grid gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Parking</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">{selectedParking?.name ?? 'Parking'}</p>
                          {selectedParking?.address ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{selectedParking.address}</p> : null}
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Pojazd</p>
                          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
                            {selectedVehicle?.licensePlate} • {selectedVehicle?.brand} {selectedVehicle?.model}
                          </p>
                          {selectedVehicleTypeLabel ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">{selectedVehicleTypeLabel}</p> : null}
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4 dark:border-white/10 dark:bg-white/[0.04]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-300/75">Stawka naliczania</p>
                          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                            {selectedVehicleRate !== null ? PRICE_FORMATTER.format(selectedVehicleRate) : PRICE_FORMATTER.format(0)}
                          </p>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300/75">
                            {billingPeriodLabel ? `za ${billingPeriodLabel}` : 'Brak danych o okresie naliczania'}
                          </p>
                        </div>
                      </div>

                      {sessionActionError ? (
                        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                          {sessionActionError}
                        </div>
                      ) : null}

                      <button
                        type="button"
                        disabled={isSessionSubmitting}
                        onClick={() => void handleStartStopSession()}
                        className="mt-5 w-full rounded-2xl bg-[linear-gradient(135deg,#059669_0%,#10b981_48%,#14b8a6_100%)] px-5 py-4 text-base font-semibold text-white shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)] transition hover:scale-[1.01] hover:shadow-[0_30px_55px_-24px_rgba(16,185,129,0.75)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 disabled:hover:shadow-[0_26px_50px_-24px_rgba(16,185,129,0.65)]"
                      >
                        {isSessionSubmitting ? 'Uruchamianie...' : 'Start postoju'}
                      </button>
                    </div>
                  )
                ) : null}
              </>
              )
            ) : null}
          </div>
        </section>

    {isStopModalOpen && activeSession ? (
      <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm sm:items-center">
        <div
          className="absolute inset-0"
          onClick={() => {
            if (!isStopSubmitting) {
              setIsStopModalOpen(false);
            }
          }}
          aria-hidden="true"
        />
        <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_30px_80px_-34px_rgba(15,23,42,0.45)] dark:border-white/10 dark:bg-[#202020] dark:shadow-[0_30px_80px_-34px_rgba(0,0,0,0.8)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-slate-400 dark:text-slate-300/75">Potwierdzenie</p>
          {sessionActionError ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
              {sessionActionError}
            </div>
          ) : null}
          <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Zakończyć postój?</h2>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                if (!isStopSubmitting) {
                  setIsStopModalOpen(false);
                }
              }}
              disabled={isStopSubmitting}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-100 dark:hover:bg-white/[0.08]"
            >
              Wróć
            </button>
            <button
              type="button"
              onClick={() => void handleConfirmStopSession()}
              disabled={isStopSubmitting}
              className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:scale-[1.01] dark:bg-white dark:text-slate-900"
            >
              Tak, zakończ
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}

export default function ParkingPaymentPage() {
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,#dcfce7_0%,#f8fafc_38%,#eef2ff_100%)] px-4 py-8 text-slate-900 transition-colors dark:bg-[radial-gradient(circle_at_top,#1f3a2e_0%,#111111_28%,#090909_100%)] dark:text-slate-100 sm:px-6 sm:py-10">
      <div className="mx-auto flex w-full max-w-[460px] flex-col gap-5">
        <ParkingPaymentPanel />
      </div>
    </main>
  );
}

function formatDuration(minutes: number) {
  if (minutes < HOUR_IN_MINUTES) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / HOUR_IN_MINUTES);
  const remainingMinutes = minutes % HOUR_IN_MINUTES;

  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes} min`;
}

function formatElapsedDuration(durationMs: number) {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function snapMinutes(value: number) {
  const snapped = Math.round(value / STEP_MINUTES) * STEP_MINUTES;
  return clamp(snapped, MIN_MINUTES, MAX_MINUTES);
}

function snapMinutesWithinRange(value: number, minMinutes: number, maxMinutes: number) {
  const snapped = Math.round(value / STEP_MINUTES) * STEP_MINUTES;
  return clamp(snapped, minMinutes, maxMinutes);
}

function upsertStoredParkingSessions(
  sessions: StoredParkingPaymentSession[],
  nextSession: StoredParkingPaymentSession,
) {
  return [...sessions.filter((session) => session.id !== nextSession.id), nextSession];
}

function resolveStoredParkingSessionForContext(
  sessions: StoredParkingPaymentSession[],
  fixedParkingId: string | null,
  selectedParkingId: string,
) {
  const targetParkingId = fixedParkingId ?? normalizeIdValue(selectedParkingId);

  if (targetParkingId) {
    return pickLatestStoredParkingSessionForParking(sessions, targetParkingId);
  }

  return sessions.length === 1 ? sessions[0] : null;
}

function pickLatestStoredParkingSessionForParking(
  sessions: StoredParkingPaymentSession[],
  parkingId: string,
) {
  const matchingSessions = sessions
    .filter((session) => normalizeIdValue(session.parkingId) === parkingId)
    .sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt));

  return matchingSessions[0] ?? null;
}

function normalizeIdValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed.toLowerCase() : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function roundCurrency(value: number) {
  return Math.round(value * 100) / 100;
}

function degreesToRadians(value: number) {
  return (value * Math.PI) / 180;
}

function radiansToDegrees(value: number) {
  return (value * 180) / Math.PI;
}

function ClockIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.75v4.75l3 1.75" />
    </svg>
  );
}
